import { Link } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { CancelSubscriptionButton } from "@/components/account/CancelSubscriptionButton";
import { useEnrollments } from "@/hooks/useEnrollments";
import { formatDate } from "@/lib/utils";
import type { EnrollmentDto } from "@/types/api";

/**
 * Hero section on /billing. One card per live plan — an account can hold
 * several subscriptions at once (e.g. Hosting Growth + Email Pro), and each
 * needs its own renewal info and its own Cancel. Ended plans stay out of the
 * way in the history card below.
 */
export function ActiveSubscriptionsList() {
  const { data, isLoading } = useEnrollments();

  if (isLoading) return <ListSkeleton />;

  const live = (data ?? []).filter(e => !isEnded(e.status));

  if (live.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardDescription className="font-mono text-[11px] uppercase tracking-wider text-brand-accentDark">
            Your plans
          </CardDescription>
          <CardTitle className="text-2xl sm:text-3xl">No active subscription</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-muted-foreground">
            Pick a plan to start provisioning domains, mailboxes and hosting.
          </p>
          <Button asChild>
            <Link to="/plans">
              <Sparkles className="h-4 w-4" />
              See plans
            </Link>
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {live.map(e => <SubscriptionCard key={e.subscription_id} e={e} />)}
    </div>
  );
}

function SubscriptionCard({ e }: { e: EnrollmentDto }) {
  const active = e.status === "active";

  return (
    <Card>
      <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-6">
        <div className="min-w-0">
          <CardDescription className="font-mono text-[11px] uppercase tracking-wider text-brand-accentDark">
            Plan
          </CardDescription>
          <CardTitle className="mt-1 truncate text-xl sm:text-2xl">{e.plan_name}</CardTitle>
          <p className="mt-1 text-sm text-muted-foreground">
            {capitalize(e.plan_type)} plan · {cycleLabel(e.billing_months)} billing
          </p>
        </div>
        <Badge variant={active ? "success" : "outline"} className="self-start uppercase">
          {e.status}
        </Badge>
      </CardHeader>
      <CardContent className="border-t border-border pt-5 sm:pt-6 space-y-4">
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <Field label="Price"   value={price(e)} />
          <Field label="Started" value={formatDate(e.started_at)} />
          <Field label="Renews"  value={formatDate(e.renews_at ?? undefined)} />
          <Field
            label="Domains"
            value={`${e.domain_count} of ${e.domain_capacity < 0 ? "∞" : e.domain_capacity}`}
          />
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button asChild variant="outline" className="sm:w-auto">
            <Link to={`/plans/${e.subscription_id}`}>
              View plan
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
          <Button asChild variant="ghost" className="sm:w-auto">
            <Link to="/plans">
              <Sparkles className="h-4 w-4" />
              Change plan
            </Link>
          </Button>
          <CancelSubscriptionButton subscriptionId={e.subscription_id} planName={e.plan_name} />
        </div>
      </CardContent>
    </Card>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="mt-0.5 text-sm font-medium text-foreground">{value}</p>
    </div>
  );
}

function isEnded(status: string) {
  const s = (status || "").toLowerCase();
  return s === "cancelled" || s === "expired";
}

function price(e: EnrollmentDto) {
  const amount = e.billing_months === 12 ? e.price_annually
    : e.billing_months === 3 ? e.price_quarterly : e.price_monthly;
  const cadence = e.billing_months === 12 ? "/yr" : e.billing_months === 3 ? "/qtr" : "/mo";
  const sym = e.currency === "USD" ? "$" : `${e.currency} `;
  return `${sym}${amount.toFixed(2)}${cadence}`;
}

function capitalize(s: string) { return s.charAt(0).toUpperCase() + s.slice(1); }
function cycleLabel(m: number) { return m === 12 ? "Annual" : m === 3 ? "Quarterly" : "Monthly"; }

function ListSkeleton() {
  return (
    <div className="space-y-4">
      {Array.from({ length: 2 }).map((_, i) => (
        <Card key={i}>
          <CardHeader>
            <Skeleton className="h-3 w-16" />
            <Skeleton className="mt-2 h-7 w-48" />
            <Skeleton className="mt-1 h-4 w-32" />
          </CardHeader>
          <CardContent className="border-t border-border pt-5 sm:pt-6 space-y-4">
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              {Array.from({ length: 4 }).map((_, j) => (
                <div key={j} className="space-y-1">
                  <Skeleton className="h-3 w-16" />
                  <Skeleton className="h-4 w-24" />
                </div>
              ))}
            </div>
            <Skeleton className="h-10 w-64" />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
